import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export type ReturnStatus =
  | 'DRAFT'
  | 'PENDING_APPROVAL'
  | 'APPROVED'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'REJECTED'
  | 'REQUIRES_RECONCILIATION';

export interface ReturnListRow {
  id: string;
  returnNumber: number;
  returnNo: string | null;
  status: ReturnStatus;
  returnType: 'FULL' | 'PARTIAL' | 'EXCHANGE';
  reason: string;
  reasonNote: string | null;
  refundDocType: 'SAME_DAY_REVERSAL' | 'CREDIT_NOTE';
  creditNoteNo: string | null;
  subTotalReturned: string | number;
  gstReversed: string | number;
  grandTotalRefunded: string | number;
  storeId: string;
  cashierId: string;
  customerId: string | null;
  returnedAt: string;
  reconciliationReason: string | null;
  reconciledAt: string | null;
  originalBilling: {
    id: string;
    billNo: string | null;
    billingDate: string;
    grandTotal: string | number;
  };
  cashier: { id: string; name: string; username: string };
  approver: { id: string; name: string; username: string } | null;
  customer: { id: string; name: string; phone: string | null } | null;
  items: Array<{ id: string; quantityReturned: number }>;
}

export interface ReturnDetail extends ReturnListRow {
  // Drill-down includes full nested data.
  originalBilling: ReturnListRow['originalBilling'] & {
    clientId: string | null;
    customerId: string | null;
    appliedOfferType: string | null;
    appliedOfferName: string | null;
    products: Array<{
      id: string;
      quantity: number;
      price: string | number;
      discount: string | number;
      gstAmount: string | number;
      total: string | number;
      variant: { id: string; product: { name: string; sku?: string } };
    }>;
    payments: Array<{ id: string; mode: string; amount: string | number }>;
    customer: ReturnListRow['customer'];
    store: { id: string; name: string; address: string; gstin: string | null };
  };
  items: Array<{
    id: string;
    quantityReturned: number;
    unitPriceCharged: string | number;
    lineRefund: string | number;
    unitGstReversed: string | number;
    disposition: string;
    offerOverrideUsed: boolean;
    offerOverrideNote: string | null;
    variant: { id: string; product: { name: string } };
    originalBillingItem: { id: string; quantity: number };
  }>;
  refunds: Array<{
    id: string;
    mode: string;
    amount: string | number;
    originalPaymentId: string | null;
    asyncState: string | null;
  }>;
  reconciler: { id: string; name: string; username: string } | null;
}

export interface ReturnListResponse {
  items: ReturnListRow[];
  total: number;
  page: number;
  limit: number;
  pageCount: number;
}

export interface InboxCounts {
  PENDING_APPROVAL: number;
  REQUIRES_RECONCILIATION: number;
}

export interface ReturnsSummary {
  /** Number of COMPLETED returns in the filtered window. */
  count: number;
  /** Total amount refunded, as a decimal string (rupees). */
  totalRefunded: string;
}

export interface ReturnsListFilters {
  storeId?: string;
  cashierId?: string;
  customerId?: string;
  status?: ReturnStatus[];
  query?: string;
  startDate?: string;
  endDate?: string;
}

@Injectable({ providedIn: 'root' })
export class ReturnsService {
  constructor(private http: HttpClient) {}

  list(
    pageOpts: { page: number; limit: number },
    filters: ReturnsListFilters = {},
  ): Observable<ReturnListResponse> {
    let params = new HttpParams()
      .set('page', pageOpts.page)
      .set('limit', pageOpts.limit);
    if (filters.storeId) params = params.set('storeId', filters.storeId);
    if (filters.cashierId) params = params.set('cashierId', filters.cashierId);
    if (filters.customerId) params = params.set('customerId', filters.customerId);
    if (filters.query) params = params.set('query', filters.query);
    if (filters.startDate) params = params.set('startDate', filters.startDate);
    if (filters.endDate) params = params.set('endDate', filters.endDate);
    if (filters.status && filters.status.length) {
      params = params.set('status', filters.status.join(','));
    }
    return this.http.get<ReturnListResponse>(environment.returns, { params });
  }

  detail(id: string): Observable<ReturnDetail> {
    return this.http.get<ReturnDetail>(`${environment.returns}/${id}`);
  }

  inboxCounts(): Observable<InboxCounts> {
    return this.http.get<InboxCounts>(environment.returnsInboxCounts);
  }

  /**
   * Aggregate count + total refunded (COMPLETED by default) for dashboard cards.
   * Same filters as list() so a date-scoped report and its returns card agree.
   */
  summary(filters: ReturnsListFilters = {}): Observable<ReturnsSummary> {
    let params = new HttpParams();
    if (filters.storeId) params = params.set('storeId', filters.storeId);
    if (filters.startDate) params = params.set('startDate', filters.startDate);
    if (filters.endDate) params = params.set('endDate', filters.endDate);
    if (filters.status && filters.status.length) {
      params = params.set('status', filters.status.join(','));
    }
    return this.http.get<ReturnsSummary>(`${environment.returns}/summary`, { params });
  }

  reconcile(
    id: string,
    payload: { action: 'APPROVE' | 'REJECT' | 'ESCALATE'; note?: string },
  ): Observable<ReturnListRow> {
    return this.http.post<ReturnListRow>(`${environment.returns}/${id}/reconcile`, payload);
  }

  cancel(id: string, reason: string): Observable<ReturnListRow> {
    return this.http.post<ReturnListRow>(`${environment.returns}/${id}/cancel`, { reason });
  }
}
